import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { IntegrationKey } from "@erp/shared-types";

/**
 * Ingest EDI Translation Lists from Famous Step
 *
 * Populates the dedicated edi_ translation-list tables from Famous Oracle:
 *   - edi_translation_lists            <- FC_TRANSLATE
 *   - edi_translation_list_items       <- FC_TRANSLATE_PRODUCT
 *   - edi_translation_list_assignments <- FC_NAME_LOCATION.PRODUCTLISTIDX
 *
 * Backend-only; requires Famous database access. Intended to run on a schedule
 * (or manually) so inbound EDI (850/880) product matching has fresh lists.
 */
export const ingestEdiTranslationListsStep = defineStepConfig({
  type: "ingest_edi_translation_lists",
  name: "Ingest EDI Translation Lists from Famous",
  description:
    "Sync Famous translation lists, items, and location assignments into the edi_ tables",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({}),

  outputSchema: z.object({
    customersResolved: z
      .number()
      .describe(
        "Distinct Famous customers (NAMEIDX) with a real-list assignment. Source-side count from Famous — not the number matched to our locations (see assignmentsUnmatchedLocation for match failures).",
      ),
    listsUpserted: z.number().describe("Translation lists created/updated"),
    itemsUpserted: z
      .number()
      .describe("Translation list items created/updated"),
    itemsUnmatchedProduct: z
      .number()
      .describe("Items whose Famous PRODUCTIDX did not resolve to a variant"),
    assignmentsUpserted: z
      .number()
      .describe("Location -> list assignments created/updated"),
    assignmentsUnmatchedLocation: z
      .number()
      .describe("Assignments whose Famous location did not resolve to ours"),
    itemsDeactivated: z
      .number()
      .describe(
        "Items deactivated because Famous no longer returns them for a refreshed list (the product was removed from the customer's list)",
      ),
    fapiItemsDeactivated: z
      .number()
      .describe(
        "Items deactivated because they belong to Famous's internal FAPI catalog (only non-zero on a tenant where an older run ingested it)",
      ),
    fapiAssignmentsRemoved: z
      .number()
      .describe(
        "Location assignments removed because they pointed at a FAPI list",
      ),
  }),

  configSchema: z.object({
    source: z
      .enum(["environment", "production"])
      .nullable()
      .default(null)
      .describe(
        "Famous database to read. Environment uses the workflow worker's normal Famous connection; production uses the guarded FAMOUS_PROD_* override. Null preserves legacy behavior: production override when enabled, otherwise environment.",
      ),
    replaceExisting: z
      .boolean()
      .default(false)
      .describe(
        "Development-only: atomically replace this organization's existing EDI translation-list data with the newly fetched Famous data.",
      ),
    // No `includeFapi` option: FAPI is Famous's internal catalog, replicated
    // across every customer, and no customer's EDI translations live in it. EDI
    // product matching resolves solely from these tables, so including it would
    // make ~460k internal rows matchable for every customer at once.
    customerOriginIds: z
      .array(z.string().trim())
      .nullable()
      .default(null)
      .describe(
        "Optional Famous customer origin ids (NAMEIDX) to scope the location->list assignments to. Lists + items are always synced in full (there are few and they're small). When null, all real-list assignments are synced.",
      ),
    timeoutMinutes: z
      .number()
      .int()
      .positive()
      // Must stay below the 55-minute activity startToCloseTimeout below so the
      // fail-fast handler timeout is what fires first (with a clear message),
      // not Temporal's generic activity timeout.
      .max(50)
      // Nullable so clearing the UI field (which stores null) still parses;
      // the handler treats null as "use the default" (config.timeoutMinutes ?? 15).
      .nullable()
      .default(15)
      .describe(
        "Fail the step if the whole ingest (incl. the Famous Oracle connect/read) exceeds this many minutes, instead of hanging until the activity timeout. Raise it for very large translation lists (max 50).",
      ),
  }),

  execution: {
    startToCloseTimeout: "55 minutes",
  },

  emits: [] as const,
});
